import { useState } from "react";
import PageHeader from "@/components/PageHeader";
import DataTable from "@/components/DataTable";
import OperationDialog from "@/components/OperationDialog";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Truck, Plus, Send } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useOptions } from "@/lib/useOptions";
import api from "@/lib/api";
import { toast } from "sonner";

export default function Outward() {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const warehouses = useOptions("/warehouses");
  const customers = useOptions("/customers");

  const ship = async (row, reload) => {
    try { await api.post(`/dispatch/${row.id}/ship`); toast.success(`${row.dispatch_no} shipped — stock deducted`); reload(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cols = [
    { key: "dispatch_no", label: "Dispatch No", mono: true },
    { key: "customer_name", label: "Customer" },
    { key: "vehicle_no", label: "Vehicle", mono: true },
    { key: "tracking_no", label: "Tracking", mono: true },
    { key: "items", label: "Lines", render: (r) => (r.items || []).length },
    { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
  ];

  const fields = [
    { name: "customer_name", label: "Customer", type: "select", required: true, options: customers.map((c) => ({ value: c.label, label: c.label })) },
    { name: "warehouse_id", label: "Warehouse", type: "select", required: true, options: warehouses },
    { name: "vehicle_no", label: "Vehicle No" },
    { name: "driver_name", label: "Driver Name" },
    { name: "tracking_no", label: "Tracking No", full: true },
  ];

  return (
    <div>
      <PageHeader title="Outward / Dispatch" subtitle="Packing, loading, shipment & proof of dispatch" icon={Truck}
        breadcrumb={["Home", "Operations", "Outward"]}
        actions={can("outward.create") && (
          <Button data-testid="add-dispatch-btn" onClick={() => setOpen(true)} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-1.5" />New Dispatch
          </Button>
        )} />
      <DataTable endpoint="/dispatch" columns={cols} testId="dispatch-table" refreshKey={refresh}
        actions={(row, reload) => row.status === "pending" && can("outward.update") && (
          <Button size="sm" variant="outline" data-testid={`ship-dispatch-${row.id}`} onClick={() => ship(row, reload)}
            className="text-blue-600 border-blue-200 hover:bg-blue-50">
            <Send className="h-4 w-4 mr-1" />Ship
          </Button>
        )} />
      <OperationDialog open={open} onOpenChange={setOpen} title="New Dispatch" headerFields={fields}
        endpoint="/dispatch" onSaved={() => setRefresh((r) => r + 1)} />
    </div>
  );
}
